import { localDateKey, localDateKeyFromIso } from './dateKey';
import { getTrainingPrefs, isScheduledRecoveryDay } from './trainingPrefs';
import type { Workout } from '../types';

const MEMORY_KEY = 'preformiq_coach_memory';

export type CoachMemory = {
  summary: string;
  updatedAt: string;
};

export type CoachContext = {
  greeting: string;
  headline: string;
  subline: string;
  suggestions: string[];
  memory: CoachMemory | null;
  emptyState: boolean;
};

type NutritionDay = {
  totals?: { calories?: number; proteinG?: number } | null;
  targets?: { proteinG?: number } | null;
};

function timeGreeting(name: string): string {
  const h = new Date().getHours();
  const first = name.split(' ')[0] || name;
  if (h < 12) return `Good morning, ${first}`;
  if (h < 17) return `Good afternoon, ${first}`;
  return `Good evening, ${first}`;
}

export function loadCoachMemory(userId?: string): CoachMemory | null {
  try {
    const raw = localStorage.getItem(userId ? `${MEMORY_KEY}:${userId}` : MEMORY_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CoachMemory;
  } catch {
    return null;
  }
}

export function saveCoachMemory(userId: string | undefined, summary: string) {
  const entry: CoachMemory = { summary, updatedAt: new Date().toISOString() };
  localStorage.setItem(userId ? `${MEMORY_KEY}:${userId}` : MEMORY_KEY, JSON.stringify(entry));
  return entry;
}

export function buildCoachContext(opts: {
  displayName: string | null;
  userId?: string;
  workouts: Workout[];
  nutrition: NutritionDay | null;
  sleepHours: number | null;
}): CoachContext {
  const name = opts.displayName?.trim() || 'there';
  const prefs = getTrainingPrefs(opts.userId);
  const recoveryDay = isScheduledRecoveryDay(false, prefs);
  const todayKey = localDateKey();
  const workoutsToday = opts.workouts.filter((w) => localDateKeyFromIso(w.startedAt) === todayKey);
  const hasWorkouts = opts.workouts.length > 0;
  const memory = loadCoachMemory(opts.userId);

  const proteinTarget = opts.nutrition?.targets?.proteinG ?? 0;
  const proteinNow = opts.nutrition?.totals?.proteinG ?? 0;
  const proteinGap = proteinTarget > 0 ? Math.max(0, Math.round(proteinTarget - proteinNow)) : 0;

  const suggestions: string[] = [];

  if (!hasWorkouts) {
    suggestions.push(
      'Build my first workout',
      'What should beginners track?',
      'How do I set protein targets?',
      'Explain recovery for new lifters',
    );
  } else {
    if (workoutsToday.length) suggestions.push("Why was today's workout harder?");
    if (proteinGap >= 30) suggestions.push('Am I eating enough protein?');
    if (recoveryDay) suggestions.push('Review my recovery');
    suggestions.push("Plan tomorrow's workout");
    const recentBench = opts.workouts.some((w) =>
      (Array.isArray(w.exercises) ? w.exercises : []).some((e) =>
        String(e?.name ?? '')
          .toLowerCase()
          .includes('bench'),
      ),
    );
    if (recentBench) suggestions.push('Help me break my bench plateau');
    suggestions.push('Analyze my training consistency');
  }

  const unique = [...new Set(suggestions)].slice(0, 5);

  let headline = 'What do you want to improve today?';
  let subline = 'Ask anything about training, nutrition, or recovery.';

  if (!hasWorkouts) {
    headline = 'Your coach is ready when you are';
    subline = "Once you log workouts I'll begin learning your habits.";
  } else if (opts.sleepHours != null && opts.sleepHours < 6.5) {
    const h = Math.floor(opts.sleepHours);
    const m = Math.round((opts.sleepHours - h) * 60);
    headline = `You slept ${h}h ${m}m`;
    subline = "Let's adjust today's training.";
  } else if (proteinGap >= 30) {
    headline = `You're ${proteinGap}g short on protein`;
    subline = 'I can help you close the gap before tonight.';
  } else if (recoveryDay && !workoutsToday.length) {
    headline = 'Recovery today';
    subline = "Let's maximize tomorrow.";
  } else if (!recoveryDay && !workoutsToday.length) {
    headline = 'Ready to build strength today?';
    subline = 'Start a session or ask for a plan tweak.';
  } else if (workoutsToday.length) {
    headline = 'Solid work today';
    subline = 'Review the session or plan what comes next.';
  }

  return {
    greeting: timeGreeting(name),
    headline,
    subline,
    suggestions: unique,
    memory,
    emptyState: !hasWorkouts,
  };
}
